import { useQueries, useQuery } from '@tanstack/react-query';

import * as TourInventoryService from '~/services/TourInventoryService';

import { DepartureInventory } from '~/types/tours/inventory';

import { deriveDepartureInventory } from '~/utils/tours/departureInventory';

export const inventoryQueryKeys = {
  all: ['tour-inventory'] as const,
  departure: (serviceId: string | undefined, packageDepartureId: string | undefined) =>
    [...inventoryQueryKeys.all, 'departure', serviceId ?? '', packageDepartureId ?? ''] as const,
};

/** Inventory is keyed by service and package departure, so both are required to run. */
export function useGetDepartureInventory(service: { serviceId?: string; packageDepartureId?: string }) {
  const { serviceId, packageDepartureId } = service;

  return useQuery({
    queryKey: inventoryQueryKeys.departure(serviceId, packageDepartureId),
    queryFn: ({ signal }) =>
      TourInventoryService.getDepartureInventory({ serviceId, departureId: packageDepartureId }, { signal }),
    enabled: !!serviceId && !!packageDepartureId,
    select: deriveDepartureInventory,
  });
}

/** Fetch and combine the tour configurations represented by one departure dropdown item. */
export function useGetDeparturesInventory(service: { serviceId?: string; packageDepartureIds: Array<string> }) {
  const { serviceId, packageDepartureIds } = service;

  return useQueries({
    queries: packageDepartureIds.map((packageDepartureId) => ({
      queryKey: inventoryQueryKeys.departure(serviceId, packageDepartureId),
      queryFn: ({ signal }) =>
        TourInventoryService.getDepartureInventory(
          { serviceId: serviceId!, departureId: packageDepartureId },
          { signal },
        ),
      enabled: !!serviceId,
      select: deriveDepartureInventory,
    })),
    combine: (results) => {
      const inventories = results.flatMap((result) => result.data ?? []);
      const configurations = new Map<string, DepartureInventory['configurations'][number]>();

      inventories.forEach((inventory) => {
        inventory.configurations.forEach((configuration) => {
          const existing = configurations.get(configuration.packageConfigurationId);
          configurations.set(configuration.packageConfigurationId, {
            ...configuration,
            units: (existing?.units ?? 0) + configuration.units,
          });
        });
      });

      const data = inventories.length
        ? {
            serviceId: inventories[0].serviceId,
            departureId: packageDepartureIds.join(','),
            season: inventories[0].season,
            units: inventories.reduce((total, inventory) => total + inventory.units, 0),
            configurations: Array.from(configurations.values()),
            entries: inventories
              .flatMap((inventory) => inventory.entries)
              .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt)),
          }
        : undefined;

      return {
        data,
        isLoading: results.some((result) => result.isLoading),
        error: results.find((result) => result.error)?.error,
      };
    },
  });
}

/** Fetch and combine one departure group's inventory across several supplier services. */
export function useGetServicesDeparturesInventory(input: {
  serviceIds: Array<string>;
  packageDepartureIds: Array<string>;
}) {
  const requests = input.serviceIds.flatMap((serviceId) =>
    input.packageDepartureIds.map((packageDepartureId) => ({ serviceId, packageDepartureId })),
  );

  return useQueries({
    queries: requests.map(({ serviceId, packageDepartureId }) => ({
      queryKey: inventoryQueryKeys.departure(serviceId, packageDepartureId),
      queryFn: ({ signal }) =>
        TourInventoryService.getDepartureInventory({ serviceId, departureId: packageDepartureId }, { signal }),
      select: deriveDepartureInventory,
    })),
    combine: (results) => {
      const inventories = results.flatMap((result) => result.data ?? []);
      const configurations = new Map<string, DepartureInventory['configurations'][number]>();

      inventories.forEach((inventory) => {
        inventory.configurations.forEach((configuration) => {
          const existing = configurations.get(configuration.packageConfigurationId);
          configurations.set(configuration.packageConfigurationId, {
            ...configuration,
            units: (existing?.units ?? 0) + configuration.units,
          });
        });
      });

      const data = inventories.length
        ? {
            serviceId: input.serviceIds.join(','),
            departureId: input.packageDepartureIds.join(','),
            season: inventories[0].season,
            units: inventories.reduce((total, inventory) => total + inventory.units, 0),
            configurations: Array.from(configurations.values()),
            entries: inventories
              .flatMap((inventory) =>
                inventory.entries.map((entry) => ({ ...entry, id: `${inventory.serviceId}:${entry.id}` })),
              )
              .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt)),
          }
        : undefined;
      const inventoriesByService = Object.fromEntries(
        input.serviceIds.flatMap((serviceId) => {
          const serviceInventories = inventories.filter((inventory) => inventory.serviceId === serviceId);
          if (!serviceInventories.length) {
            return [];
          }

          const serviceConfigurations = new Map<string, DepartureInventory['configurations'][number]>();
          serviceInventories.forEach((serviceInventory) => {
            serviceInventory.configurations.forEach((configuration) => {
              const existing = serviceConfigurations.get(configuration.packageConfigurationId);
              serviceConfigurations.set(configuration.packageConfigurationId, {
                ...configuration,
                units: (existing?.units ?? 0) + configuration.units,
              });
            });
          });

          return [
            [
              serviceId,
              {
                serviceId,
                departureId: input.packageDepartureIds.join(','),
                season: serviceInventories[0].season,
                units: serviceInventories.reduce((total, serviceInventory) => total + serviceInventory.units, 0),
                configurations: Array.from(serviceConfigurations.values()),
                entries: serviceInventories
                  .flatMap((serviceInventory) => serviceInventory.entries)
                  .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt)),
              },
            ],
          ];
        }),
      ) as Record<string, DepartureInventory>;

      return {
        data,
        inventoriesByService,
        isLoading: results.some((result) => result.isLoading),
        error: results.find((result) => result.error)?.error,
      };
    },
  });
}
